import { setTokenGetter } from "@/api/coreApi";
import { Auth0Provider, useAuth0, User } from "@auth0/auth0-react";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router";

interface AuthContextType {
  user: User | undefined;
  isAuthenticated: boolean;
  isLoading: boolean;
  roles: string[];
  isInitialized: boolean;
  primaryRole: string | null;
  orgId: string | null;
  orgName: string | null;
  appRoles: string[];
  orgRoles: string[];
}

const defaultAuth: AuthContextType = {
  user: undefined,
  isAuthenticated: false,
  isLoading: false,
  roles: [],
  isInitialized: false,
  primaryRole: null,
  orgId: null,
  orgName: null,
  appRoles: [],
  orgRoles: [],
};

const AuthContext = createContext<AuthContextType>(defaultAuth);
const audience = import.meta.env.VITE_AUTH_AUDIENCE;
const AUTH_SCOPE = "openid profile email offline_access";

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();

  const domain = import.meta.env.VITE_AUTH_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH_CLIENT_ID;
  const redirectUri = import.meta.env.VITE_AUTH_CALLBACK_URL;

  const onRedirectCallback = (appState: any) => {
    navigate(appState?.returnTo || window.location.pathname);
  };

  if (!(domain && clientId && redirectUri)) {
    console.error("Missing Auth0 configuration");
    return <div>Auth0 configuration error</div>;
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: redirectUri,
        scope: AUTH_SCOPE,
        audience: audience,
      }}
      onRedirectCallback={onRedirectCallback}
      useRefreshTokens={true}
      cacheLocation="localstorage"
    >
      <AuthContextProvider>{children}</AuthContextProvider>
    </Auth0Provider>
  );
};

const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  const {
    user,
    isAuthenticated,
    isLoading,
    getIdTokenClaims,
    logout,
    getAccessTokenSilently,
  } = useAuth0();
  const [roles, setRoles] = useState<string[]>([]);
  const [appRoles, setAppRoles] = useState<string[]>([]);
  const [orgRoles, setOrgRoles] = useState<string[]>([]);
  const [primaryRole, setPrimaryRole] = useState<string | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const navigate = useNavigate();

  // Set up token getter for coreApi
  useEffect(() => {
    const tokenGetterFn = async (): Promise<string | null> => {
      if (!isAuthenticated) {
        return null;
      }
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: audience,
            scope: AUTH_SCOPE,
          },
        });
        return token;
      } catch (error: any) {
        console.error("Failed to get access token:", error);

        // If refresh token is invalid, clear auth and force re-login
        if (
          error.error === "invalid_grant" ||
          error.message?.includes("invalid") ||
          error.message?.includes("Unknown or invalid refresh token")
        ) {
          console.warn("Invalid refresh token - clearing auth state");
          localStorage.removeItem("authToken");
          localStorage.removeItem("idToken");

          // Trigger logout
          logout({
            logoutParams: {
              returnTo: window.location.origin + "/login?error=session_expired",
            },
          });

          return null;
        }

        // For other errors, don't fall back to potentially expired token
        return null;
      }
    };

    setTokenGetter(tokenGetterFn);
  }, [isAuthenticated, getAccessTokenSilently, logout]);

  useEffect(() => {
    const initializeAuth = async () => {
      if (isLoading) {
        return;
      }

      try {
        if (isAuthenticated) {
          try {
            const accessToken = await getAccessTokenSilently({
              authorizationParams: {
                audience: audience,
                scope: AUTH_SCOPE,
              },
            });
            localStorage.setItem("authToken", accessToken);

            const claims = await getIdTokenClaims();

            if (claims) {
              localStorage.setItem("idToken", claims?.__raw || "");
              // Extract app roles (regular Auth0 roles)
              const appRolesClaim =
                claims["https://crisislinguistics.no/roles"];
              const extractedAppRoles = Array.isArray(appRolesClaim)
                ? appRolesClaim.map((role) => role.toLowerCase())
                : appRolesClaim
                  ? [appRolesClaim.toLowerCase()]
                  : [];

              // Check if user has any roles - if not, log them out
              if (extractedAppRoles.length === 0) {
                console.warn("User has no roles assigned - logging out");
                localStorage.removeItem("authToken");
                localStorage.removeItem("idToken");
                logout({
                  logoutParams: {
                    returnTo: window.location.origin + "/login?error=no_roles",
                  },
                });
                return;
              }

              // Set roles and other auth data
              setAppRoles(extractedAppRoles);
              setRoles(extractedAppRoles);

              // Determine primary role based on hierarchy
              const primaryRole = determinePrimaryRole(extractedAppRoles);
              setPrimaryRole(primaryRole);
            } else {
              // Clear all data if no claims
              clearAuthData();
            }
          } catch (tokenError: any) {
            // Handle missing refresh token specifically
            if (
              tokenError.error === "missing_refresh_token" ||
              tokenError.message?.includes("Missing Refresh Token")
            ) {
              console.warn(
                "Refresh token missing - initiating re-authentication",
              );
              clearAuthData();
              localStorage.removeItem("authToken");
              localStorage.removeItem("idToken");

              logout({
                logoutParams: {
                  returnTo:
                    window.location.origin + "/login?error=session_expired",
                },
              });
              return;
            }
            throw tokenError;
          }
        } else {
          clearAuthData();
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        clearAuthData();
      } finally {
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, [
    isAuthenticated,
    isLoading,
    getIdTokenClaims,
    logout,
    getAccessTokenSilently,
  ]);

  const clearAuthData = () => {
    setRoles([]);
    setAppRoles([]);
    setOrgRoles([]);
    setPrimaryRole(null);
    setOrgId(null);
    setOrgName(null);
  };

  const determinePrimaryRole = (userRoles: string[]): string | null => {
    // Define role hierarchy (highest to lowest priority)
    const hierarchy = [
      "admin",
      "course-author",
      "manager",
      "client-manager",
      "client-trainee",
    ];

    // Find the highest priority role
    for (const role of hierarchy) {
      if (userRoles.includes(role)) {
        return role;
      }
    }

    // Return first role if none match hierarchy
    return userRoles.length > 0 ? userRoles[0] : null;
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading: isLoading || !isInitialized,
    roles,
    appRoles,
    orgRoles,
    isInitialized,
    primaryRole,
    orgId,
    orgName,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth0Context = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth0Context must be used within an AuthProvider");
  }
  return context;
};
